import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { 
  Form, 
  Input, 
  TextArea, 
  ButtonLink,
  Button,
  BlogRichTextEditor, 
  RichTextEditor,
} from '../../common/FormItems';
import { 
  withTranslation, 
  withFormValidation,
  withMsgInfo,
} from '../../hoc';
import { useBlogCategories } from '../../../hooks/useBlog';
import { 
  createBlogPost, 
  updateBlogPost, 
  getBlogPostBySlug,
} from '../../../actions';
import Swal from 'sweetalert2';

const Wrapper = styled.div`
  width: 90%;
  margin: 1% auto auto 3.7%;
  padding: 0 20px;
`;

const FormWrapper = styled.div`
  background: var(--admincat-color-grey-1);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const StyledButtonLink = styled(ButtonLink)`
  margin-bottom: 1.5rem;
  
  span {
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h3`
  color: var(--admincat-color-grey-2);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 2rem 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--admincat-color-grey-3);
`;

const ToggleSection = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid var(--admincat-color-grey-3);
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const SwitchLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: var(--admincat-color-grey-2);
  margin: 0;
`;

const SwitchToggle = styled.div`
  position: relative;
  width: 50px;
  height: 26px;
  background: ${props => props.checked ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' : '#ccc'};
  border-radius: 34px;
  cursor: pointer;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${props => props.checked ? '27px' : '3px'};
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const HelpText = styled.p`
  font-size: 0.9rem;
  color: var(--admincat-color-grey-3);
  margin: 0.5rem 0 0 0;
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  position: relative;
  display: inline-block;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    object-fit: cover;
    display: block;
  }
  
  button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(220, 53, 69, 0.9);
    color: white;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    line-height: 1;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(200, 35, 51, 1);
      transform: scale(1.1);
    }
  }
`;

const BlogForm = (props) => {
  const { tr, setMsgInfo, initialData } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const isEdit = !!slug;
  
  // Use a callback ref pattern to get the editor instance
  const editorInstanceRef = useRef(null);

  const { categories, isLoading: categoriesLoading } = useBlogCategories();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    featured_image: null,
    meta_title: '',
    meta_description: '',
    tags: '',
    is_featured: false,
    status: 'draft',
  });

  // Store the latest content in a ref to avoid stale closures
  const contentRef = useRef('');

  useEffect(() => {
    contentRef.current = formData.content;
  }, [formData.content]);

  useEffect(() => {
    if (initialData) {
      const newFormData = {
        title: initialData.title || '',
        content: initialData.content || '',
        excerpt: initialData.excerpt || '',
        category: initialData.category?.id || '',
        featured_image: null,
        meta_title: initialData.meta_title || '',
        meta_description: initialData.meta_description || '',
        tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : (initialData.tags || ''),
        is_featured: initialData.is_featured || false,
        status: initialData.status || 'draft',
      };
      setFormData(newFormData);
      contentRef.current = newFormData.content;
      
      if (initialData.featured_image) {
        setImagePreview(initialData.featured_image);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (isEdit && slug) {
      loadPost();
    }
  }, [slug, isEdit]);

  const loadPost = async () => {
    setLoadingData(true);
    try {
      const post = await dispatch(getBlogPostBySlug(slug));
      
      if (!post) {
        Swal.fire(tr('Error'), tr('Post not found'), 'error');
        navigate('/admin/dashboard/blog/posts');
        return;
      }
      
      const newFormData = {
        title: post.title || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        category: post.category?.id || post.category || '',
        featured_image: null,
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        tags: Array.isArray(post.tags) ? post.tags.join(', ') : (post.tags || ''),
        is_featured: post.is_featured || false,
        status: post.status || 'draft',
      };
      
      setFormData(newFormData);
      contentRef.current = newFormData.content;
      
      if (post.featured_image) {
        setImagePreview(post.featured_image);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      Swal.fire(tr('Error'), tr('Failed to load blog post'), 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        Swal.fire(tr('Error'), tr('Please select a valid image file'), 'error');
        e.target.value = '';
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire(tr('Error'), tr('Image size should not exceed 5MB'), 'error');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({ ...prev, featured_image: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, featured_image: null }));
    setImagePreview(null);
    const fileInput = document.querySelector('input[name="featured_image"]');
    if (fileInput) fileInput.value = '';
  };

  const handleContentChange = useCallback((e) => {
    const htmlContent = e.target.value;
    contentRef.current = htmlContent;
    setFormData(prev => ({ ...prev, content: htmlContent }));
  }, []);

  // Callback ref for the editor instance
  const setEditorInstance = useCallback((instance) => {
    editorInstanceRef.current = instance;
  }, []);

  const getLatestContent = () => {
    // First try to get content from the editor instance
    if (editorInstanceRef.current && typeof editorInstanceRef.current.getContent === 'function') {
      const editorContent = editorInstanceRef.current.getContent();
      if (editorContent) {
        return editorContent;
      }
    }
    // Fall back to the ref
    if (contentRef.current) {
      return contentRef.current;
    }
    // Last resort, use formData
    return formData.content || '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Get the latest content from the editor
    const currentContent = getLatestContent();
    
    console.log('=== Submit Debug ===');
    console.log('Editor instance:', editorInstanceRef.current);
    console.log('Content from getLatestContent:', currentContent);
    console.log('Content from formData:', formData.content);
    console.log('Content from ref:', contentRef.current);
    
    const contentText = stripHtml(currentContent);
    const titleLength = formData.title.trim().length;
    const excerptLength = formData.excerpt.trim().length;
    const tagsLength = formData.tags.trim().length;
    
    console.log('Content text:', contentText);
    console.log('Content text length:', contentText.length);
    
    if (titleLength < 3) {
      Swal.fire(tr('Validation Error'), tr('The title must be at least 3 characters.'), 'error');
      return;
    }
    
    if (titleLength > 200) {
      Swal.fire(tr('Validation Error'), tr('The title may not be greater than 200 characters.'), 'error');
      return;
    }
    
    if (contentText.length < 10) {
      Swal.fire(tr('Validation Error'), tr('The content must be at least 10 characters.'), 'error');
      return;
    }
    
    if (excerptLength > 500) {
      Swal.fire(tr('Validation Error'), tr('The excerpt may not be greater than 500 characters.'), 'error');
      return;
    }
    
    if (!formData.category) {
      Swal.fire(tr('Validation Error'), tr('Please select a category.'), 'error');
      return;
    }

    if (tagsLength === 0) {
      Swal.fire(tr('Validation Error'), tr('Please add at least one tag.'), 'error');
      return;
    }

    setIsLoading(true);
    try {
      const submitData = new FormData();
      
      submitData.append('title', formData.title.trim());
      submitData.append('content', currentContent);  // Use the content we retrieved
      submitData.append('excerpt', formData.excerpt.trim());
      submitData.append('category', formData.category);
      submitData.append('is_featured', formData.is_featured);
      submitData.append('meta_title', formData.meta_title || '');
      submitData.append('meta_description', formData.meta_description || '');

      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(t => t);
      submitData.append('tags', JSON.stringify(tagsArray));

      if (formData.featured_image instanceof File) {
        submitData.append('featured_image', formData.featured_image);
      }
      if (formData.status) {
        submitData.append('status', formData.status);
      }

      // Debug: Log FormData contents
      console.log('=== FormData Contents ===');
      for (let [key, value] of submitData.entries()) {
        console.log(`${key}: ${value}`);
      }

      if (isEdit) {
        await dispatch(updateBlogPost(slug, submitData));
        Swal.fire(tr('Success'), tr('Blog post updated successfully'), 'success');
        navigate('/admin/dashboard/blog/posts');
      } else {
        await dispatch(createBlogPost(submitData));
        Swal.fire(tr('Success'), tr('Blog post created successfully'), 'success');
        navigate('/admin/dashboard/blog/posts');
      }
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.content?.[0] ||
        error.response?.data?.featured_image?.[0] ||
        error.response?.data?.detail || 
        error.response?.data?.non_field_errors?.[0] ||
        tr('Failed to save blog post');
      Swal.fire(tr('Error'), errorMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const categoriesData = categories?.results || categories || [];
  
  const categoryOptions = useMemo(() => 
    categoriesData
      .filter(cat => cat.is_active !== false)
      .map(cat => ({
        value: cat.id,
        label: cat.name,
        text: cat.name,
      })),
    [categoriesData]
  );

  return (
    <Wrapper>
      <StyledButtonLink
        to="/admin/dashboard/blog/posts"
        icon="angle double left"
        name={tr('Back to Posts')}
      />
      
      <FormWrapper>
        <Form
          onSubmit={props.onSubmit ? (e) => props.onSubmit(formData, e) : onSubmit}
          formData={formData}
          isLoading={props.isSubmitting || isLoading}
          loadingData={loadingData}
          title={isEdit ? tr('Edit Blog Post') : tr('Create Blog Post')}
          submitLabel={props.submitLabel || (isEdit ? tr('Update Post') : tr('Create Post'))}
        >
          <Input
            name="title"
            label={tr('Post Title')}
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            showRequiredAsterisk
            placeholder={tr('Enter an engaging title for your post')}
          />

          <TextArea
            name="excerpt"
            label={tr('Excerpt / Summary')}
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            rows={3}
            placeholder={tr('Brief summary of your post (max 500 characters)')}
          />
          <small style={{ color: 'var(--admincat-color-grey-3)' }}>
            {formData.excerpt.length}/500 {tr('characters')}
          </small>

          <BlogRichTextEditor
            editorInstanceRef={setEditorInstance}
            name="content"
            label={tr('Post Content')}
            value={formData.content}
            onChange={handleContentChange}
            required
            placeholder={tr('Write your blog post content here. You can add images, format text, and more...')}
          />
          <small style={{ color: 'var(--admincat-color-grey-3)', display: 'block', marginTop: '0.5rem' }}>
            {stripHtml(formData.content).length} {tr('characters')} ({tr('minimum 10 required')})
          </small>

          {categoriesLoading ? (
            <div style={{ padding: '1rem', color: 'var(--admincat-color-grey-3)' }}>
              {tr('Loading categories...')}
            </div>
          ) : categoryOptions.length === 0 ? (
            <div style={{ 
              padding: '1rem', 
              background: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <strong>{tr('No categories available')}</strong>
              <p style={{ margin: '0.5rem 0 0 0' }}>
                {tr('Please create a category first in the Blog Categories section.')}
              </p>
            </div>
          ) : (
            <div style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="category"
                style={{ 
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  color: 'var(--admincat-color-grey-2)'
                }}
              >
                {tr('Category')} <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={(e) => {
                  setFormData({ ...formData, category: e.target.value });
                }}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--admincat-color-grey-3)',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  background: '#fff'
                }}
              >
                <option value="">{tr('Select a category')}</option>
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '1.5rem' }}>
            <label 
              style={{ 
                display: 'block',
                fontWeight: '600',
                marginBottom: '0.5rem',
                color: 'var(--admincat-color-grey-2)'
              }}
            >
              {tr('Featured Image')}
            </label>
            <input
              type="file"
              name="featured_image"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--admincat-color-grey-3)',
                borderRadius: '4px',
                fontSize: '1rem',
                background: '#fff',
                cursor: 'pointer'
              }}
            />
            <small style={{ color: 'var(--admincat-color-grey-3)', display: 'block', marginTop: '0.25rem' }}>
              {tr('Accepted formats: JPG, PNG, GIF, WEBP. Max size: 5MB')}
            </small>
            {imagePreview && (
              <ImagePreview>
                <img src={imagePreview} alt="Featured image preview" />
                <button type="button" onClick={handleRemoveImage} title={tr('Remove image')}>
                  ×
                </button>
              </ImagePreview>
            )}
          </div>

          <Input
            name="tags"
            label={tr('Tags')}
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            placeholder={tr('technology, news, updates (comma separated)')}
            required
            showRequiredAsterisk
          />
          <small style={{ color: 'var(--admincat-color-grey-3)', display: 'block', marginTop: '0.25rem' }}>
            {tr('Separate tags with commas. Example: technology, web development, tutorials')}
          </small>

          {/* <SectionTitle>{tr('Post Settings')}</SectionTitle>
          
          <ToggleSection>
            <SwitchWrapper>
              <SwitchToggle 
                checked={formData.is_featured}
                onClick={() => setFormData({ ...formData, is_featured: !formData.is_featured })}
              />
              <SwitchLabel>{tr('Featured Post')}</SwitchLabel>
            </SwitchWrapper>
            <HelpText>
              {tr('Featured posts will be highlighted on the blog homepage')}
            </HelpText>
          </ToggleSection> */}

          <SectionTitle>{tr('SEO Settings')}</SectionTitle>

          <Input
            name="meta_title"
            label={tr('SEO Title')}
            value={formData.meta_title}
            onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
            placeholder={tr('Custom title for search engines')}
          />

          <TextArea
            name="meta_description"
            label={tr('SEO Description')}
            value={formData.meta_description}
            onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
            rows={2}
            placeholder={tr('Meta description for search engines (max 160 characters)')}
          />
          <small style={{ color: 'var(--admincat-color-grey-3)' }}>
            {formData.meta_description.length}/160 {tr('characters')}
          </small>
        </Form>
      </FormWrapper>
    </Wrapper>
  );
};

export default compose(
  withTranslation,
  withFormValidation,
  withMsgInfo,
)(BlogForm);